import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
} from '@bookaburra/ui';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { authClient } from '../auth/client';
import { fetchMyMemberships } from '../booking-flow/tenant-api';
import { AppHeader } from '../components/AppHeader';
import { formatRole } from '../lib/format-role';

interface HomeSearch {
  /** Set to `1` when Better Auth redirects here after a successful
   *  email verification (see signup.tsx callbackURL). Triggers the
   *  green "Email verified" banner. */
  verified?: boolean;
}

export const Route = createFileRoute('/')({
  validateSearch: (search: Record<string, unknown>): HomeSearch => ({
    verified: search.verified === '1' || search.verified === 1 || search.verified === true,
  }),
  component: HomePage,
});

function HomePage() {
  const { verified } = Route.useSearch();
  const { data: session, isPending } = authClient.useSession();
  const signedIn = Boolean(session?.user);

  const memberships = useQuery({
    enabled: signedIn,
    queryKey: ['me', 'memberships'],
    queryFn: fetchMyMemberships,
  });

  return (
    <div className="min-h-dvh bg-background">
      <AppHeader />
      <main className="mx-auto flex max-w-xl flex-col items-center px-6 py-12 text-center sm:py-16">
        {verified ? (
          <Alert variant="success" className="mb-8 text-left">
            <CheckCircle2 className="h-4 w-4" />
            <AlertTitle>Email verified</AlertTitle>
            <AlertDescription>
              You&rsquo;re all set — your account is active and you&rsquo;re
              signed in.
            </AlertDescription>
          </Alert>
        ) : null}
        <h1 className="text-4xl font-bold tracking-tight">
          {signedIn ? `Welcome back${session?.user.name ? `, ${session.user.name}` : ''}` : 'Bookaburra'}
        </h1>
        <p className="mt-4 text-muted-foreground">
          Smart booking for service providers.
        </p>

        {/* Signed-in workspace section */}
        {signedIn ? (
          <section className="mt-10 rounded-2xl border border-brand-sage/15 bg-card p-6 text-left shadow-brand">
            <header className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-widest text-primary">
                Your workspaces
              </p>
              <Link to="/onboarding">
                <Button variant="outline" size="sm">Add business</Button>
              </Link>
            </header>

            {memberships.isLoading ? (
              <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
            ) : memberships.data && memberships.data.memberships.length === 0 ? (
              <div className="mt-4 rounded-md border border-dashed border-border bg-secondary/40 p-5 text-sm">
                <p>You aren&rsquo;t a member of any business yet.</p>
                <p className="mt-1 text-muted-foreground">
                  Create one to start taking bookings.
                </p>
                <Link to="/onboarding">
                  <Button size="sm" className="mt-3">Set up your business</Button>
                </Link>
              </div>
            ) : (
              <ul className="mt-4 space-y-2">
                {memberships.data?.memberships.map((m) => (
                  <li key={m.tenant.id}>
                    <Link
                      to="/admin/$slug"
                      params={{ slug: m.tenant.slug }}
                      className="flex items-center justify-between rounded-md border border-border bg-secondary/30 px-4 py-3 transition-colors hover:bg-secondary"
                    >
                      <span>
                        <span className="block font-semibold">{m.tenant.name}</span>
                        <span className="text-xs text-muted-foreground">/{m.tenant.slug}</span>
                      </span>
                      <span className="flex items-center gap-2">
                        <Badge variant="secondary">{formatRole(m.role)}</Badge>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : null}

        {/* Demo booking flow links — works without signing in */}
        <section className="mt-10">
          <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Demo: try the booking flow at a sample business
          </p>
          <div className="grid gap-3 text-sm">
            <Link
              to="/book/$slug"
              params={{ slug: 'sunshine-imaging' }}
              className="rounded-lg border border-border bg-card px-5 py-4 text-left transition-colors hover:bg-secondary"
            >
              <p className="font-semibold">Sunshine Imaging</p>
              <p className="text-muted-foreground">Book a scan</p>
            </Link>
            <Link
              to="/book/$slug"
              params={{ slug: 'harbourside-medical' }}
              className="rounded-lg border border-border bg-card px-5 py-4 text-left transition-colors hover:bg-secondary"
            >
              <p className="font-semibold">Harbourside Medical</p>
              <p className="text-muted-foreground">Book a consult</p>
            </Link>
            <Link
              to="/book/$slug"
              params={{ slug: 'two-mowers' }}
              className="rounded-lg border border-border bg-card px-5 py-4 text-left transition-colors hover:bg-secondary"
            >
              <p className="font-semibold">Two Mowers Gardening</p>
              <p className="text-muted-foreground">Book a job</p>
            </Link>
          </div>
        </section>

        {/* Signed-out CTAs — signed-in users access account actions
            via the UserMenu in the top header. */}
        {!signedIn && !isPending ? (
          <section className="mt-12 border-t border-border pt-8">
            <div className="flex justify-center gap-2">
              <Link to="/signin">
                <Button variant="outline" size="sm">Sign in</Button>
              </Link>
              <Link to="/signup">
                <Button size="sm">Create account</Button>
              </Link>
            </div>
          </section>
        ) : null}
      </main>
    </div>
  );
}
